import { useState, useCallback, useMemo } from "react";

const STORAGE_KEY = "empleadosFilters";

export const ESTADOS_EMPLEADO = [
  { value: "ACTIVO", label: "Activo", color: "success" },
  { value: "INACTIVO", label: "Inactivo", color: "error" },
];

const INITIAL_FILTERS = {
  busqueda: "",
  estados: [],
  roles: [],
  sinFoto: false,
};

const normalizar = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const normalizarRut = (str) => {
  if (!str) return "";
  return str.replace(/\./g, "").replace(/-/g, "").toLowerCase();
};

/**
 * Hook para filtrar el listado de empleados.
 * Persiste estado en localStorage.
 *
 * @param {Array} empleados - Array de empleados desde RTK Query
 * @returns {{ filtros, filtrados, updateFiltro, limpiarFiltros }}
 */
export default function useEmpleadosFilters(empleados = []) {
  const [filtros, setFiltros] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return { ...INITIAL_FILTERS, ...JSON.parse(saved) };
    } catch {
      // silencioso
    }
    return INITIAL_FILTERS;
  });

  const persistir = useCallback((nuevos) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nuevos));
    } catch {
      // silencioso
    }
  }, []);

  const updateFiltro = useCallback(
    (campo, valor) => {
      setFiltros((prev) => {
        const next = { ...prev, [campo]: valor };
        persistir(next);
        return next;
      });
    },
    [persistir],
  );

  const limpiarFiltros = useCallback(() => {
    setFiltros(INITIAL_FILTERS);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  const filtrados = useMemo(() => {
    const busquedaNorm = normalizar(filtros.busqueda);
    const rutNorm = normalizarRut(filtros.busqueda);

    return empleados.filter((emp) => {
      // Búsqueda libre
      if (busquedaNorm) {
        const matchTexto =
          normalizar(emp.nombre).includes(busquedaNorm) ||
          normalizar(emp.apellidos).includes(busquedaNorm) ||
          normalizar(emp.correo).includes(busquedaNorm) ||
          normalizar(emp.cargo).includes(busquedaNorm);
        const matchRut = normalizarRut(emp.rut).includes(rutNorm);
        if (!matchTexto && !matchRut) return false;
      }

      // Estado
      if (filtros.estados.length > 0 && !filtros.estados.includes(emp.estado)) {
        return false;
      }

      // Sin foto
      if (filtros.sinFoto && emp.foto_url) {
        return false;
      }

      // Rol del empleado en la sucursal actual.
      if (filtros.roles.length > 0) {
        if (!emp.refRol || !filtros.roles.includes(emp.refRol)) return false;
      }

      return true;
    });
  }, [empleados, filtros]);

  return { filtros, filtrados, updateFiltro, limpiarFiltros };
}
